// src/features/leases/components/ConcessionCard.jsx
//
// LSE-TAB-REDESIGN Part 3 — card-style concession display that
// replaces AllowanceRow rendering on the Lease tab. AllowanceRow
// file is kept per scope guardrails (not rendered here).
//
// TI-ALLOWANCE-REDESIGN: larger summary numbers, thicker spend bar,
// forfeiture concept for expired undrawn balances, collapsed clause,
// and smart draw guard that allows historical/back-dated draws.

// INC-04, INC-06 (LSE-STYLE-NORMALIZE-1): TYPE_FG.abatement → T.amberDark,
// TYPE_FG.ti_allowance → T.spacebaseBlue.
import React, { useState } from "react";
import { T } from "../../../app/tokens.js";
import { parseDateOnly } from "../leaseTabConfig.js";
import { recordDraw, patchDraw, deleteDraw } from "../../../data/leasesClient.js";
import { ErrorBanner } from "../../../components/ErrorBanner.jsx";
import { DateInput } from "../../../components/DateInput.jsx";
import { getCurrencySymbol } from "../../../utils/format.js";
import { convertAmount }    from "../../../utils/fxConvert.js";

// ─── Type palette (mirrors AllowanceRow for visual consistency) ───────────

const TYPE_BG = {
  ti_allowance:    T.provenanceSpacebaseBg,
  moving_allowance:T.provenancePdfBg,
  abatement:       T.pillAmberBg,
  test_fit:        T.pillGreenBg,
  other:           T.warmBeige,
};
const TYPE_FG = {
  ti_allowance:    T.spacebaseBlue,  // INC-06
  moving_allowance:T.provenancePdf,
  abatement:       T.amberDark,      // INC-04
  test_fit:        T.forestGreen,
  other:           T.mid,
};
const TYPE_LABEL = {
  ti_allowance:    "TI Allowance",
  moving_allowance:"Moving",
  abatement:       "Abatement",
  test_fit:        "Test Fit",
  other:           "Other",
};

// ─── Helpers ─────────────────────────────────────────────────────────────

// fmtAmount — supports optional FX conversion via displayCcy/rates.
// Mirrors the pattern in RentScheduleCard.fmtAmt.
function fmtAmount(amount, fromCcy, displayCcy, rates) {
  if (amount == null) return "—";
  const n = parseFloat(amount);
  if (!Number.isFinite(n)) return "—";
  const from = fromCcy || "USD";
  const to   = displayCcy || from;
  const converted = (from !== to && rates)
    ? convertAmount(n, from, to, rates)
    : n;
  const eff = (converted == null || !Number.isFinite(converted)) ? n : converted;
  const ccy = (converted == null || !Number.isFinite(converted)) ? from : to;
  const sym = getCurrencySymbol(ccy);
  const prefix = sym !== ccy ? sym : "";
  return `${prefix}${Math.round(eff).toLocaleString("en-US")} ${ccy}`;
}

function fmtDate(d) {
  if (!d) return null;
  const dt = parseDateOnly(d);
  if (!dt) return String(d);
  return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// Format a Date object as YYYY-MM-DD for input[type="date"] value
function toInputDate(d) {
  if (!d) return "";
  const dt = parseDateOnly(d);
  if (!dt) return "";
  const y  = dt.getFullYear();
  const m  = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function daysFromToday(dateStr) {
  if (!dateStr) return null;
  const dt = parseDateOnly(dateStr);
  if (!dt) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((dt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

// Returns true if expiryDate is strictly before today (YYYY-MM-DD string compare).
// Both sides must normalise to YYYY-MM-DD for correctness.
export function isExpired(expiryDate) {
  if (!expiryDate) return false;
  const dt = parseDateOnly(expiryDate);
  if (!dt) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiryMidnight = new Date(dt);
  expiryMidnight.setHours(0, 0, 0, 0);
  return expiryMidnight < today;
}

export function calcRemaining(totalAmount, drawnToDate) {
  // Return null when total is unknown — avoids rendering misleading
  // negative or zero values when only drawn is set (architect finding,
  // 2026-05-21: parseFloat(null ?? 0) was silently treating null as $0).
  if (totalAmount == null) return null;
  const total = parseFloat(totalAmount);
  if (!Number.isFinite(total)) return null;
  const drawn = parseFloat(drawnToDate ?? 0);
  return total - (Number.isFinite(drawn) ? drawn : 0);
}

// ─── Shared input style ───────────────────────────────────────────────────

const INPUT_STYLE = {
  fontSize:      T.fsSm,
  border:        `1px solid ${T.border}`,
  borderRadius:   4,
  padding:       "3px 7px",
  background:     T.card,
  color:          T.dark,
  outline:        "none",
  fontFamily:     "inherit",  // TI-ALLOWANCE-REDESIGN: prevent monospace browser default on date inputs
};

// ─── DrawHistoryRow ───────────────────────────────────────────────────────
//
// Renders one draw row. When canEdit/canDelete are true, shows edit and
// delete actions. Clicking edit swaps the row for an inline edit form.

function DrawHistoryRow({ draw, currency, canEdit, canDelete, onEdit, onDelete }) {
  const [confirming, setConfirming] = useState(false);
  const [editing,    setEditing]    = useState(false);
  const [editDate,   setEditDate]   = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editDesc,   setEditDesc]   = useState("");
  const [busy,       setBusy]       = useState(false);
  const [editError,  setEditError]  = useState(null);

  const amt  = draw.amountLocalCurrency ?? draw.amountDefaultCurrency ?? draw.amount;
  const ccy  = draw.localCurrency || draw.defaultCurrency || currency || "USD";
  const date = draw.dateReceived || draw.dateRequested || draw.drawDate;

  function openEdit() {
    setEditDate(toInputDate(date) || "");
    setEditAmount(amt != null ? String(parseFloat(amt)) : "");
    setEditDesc(draw.description || "");
    setEditError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setEditError(null);
  }

  async function handleEditSave(e) {
    e.preventDefault();
    setEditError(null);

    if (!editDate) {
      setEditError(new Error("Date is required."));
      return;
    }
    const parsedAmt = parseFloat(editAmount);
    if (!editAmount || !Number.isFinite(parsedAmt) || parsedAmt <= 0) {
      setEditError(new Error("Amount must be a number greater than 0."));
      return;
    }

    setBusy(true);
    try {
      await onEdit(draw, {
        drawDate:    editDate,
        amount:      parsedAmt,
        ...(editDesc.trim() ? { description: editDesc.trim() } : { description: null }),
      });
      setEditing(false);
    } catch (err) {
      setEditError(err);
    } finally {
      setBusy(false);
    }
  }

  // ── Inline edit form ───────────────────────────────────────────────────
  if (editing) {
    return (
      <form
        data-testid="draw-edit-form"
        onSubmit={handleEditSave}
        style={{
          padding: "6px 0",
          borderTop: `0.5px solid ${T.warmBorder}`,
        }}
      >
        {editError && (
          <div style={{ marginBottom: 6 }}>
            <ErrorBanner error={editError} onDismiss={() => setEditError(null)} />
          </div>
        )}
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <DateInput
            data-testid="edit-draw-date"
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
            disabled={busy}
            style={{ ...INPUT_STYLE, width: 140 }}
          />
          <input
            data-testid="edit-draw-amount"
            type="number"
            placeholder={`Amount (${ccy})`}
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            disabled={busy}
            min="0.01"
            step="0.01"
            style={{ ...INPUT_STYLE, width: 130 }}
          />
          <input
            data-testid="edit-draw-description"
            type="text"
            placeholder="Description (optional)"
            value={editDesc}
            onChange={(e) => setEditDesc(e.target.value)}
            disabled={busy}
            style={{ ...INPUT_STYLE, flex: 1, minWidth: 100 }}
          />
          <button
            data-testid="edit-draw-save"
            type="submit"
            disabled={busy}
            style={{
              fontSize:     T.fsSm,
              fontWeight:   T.fwSemibold,
              padding:      "3px 10px",
              borderRadius:  4,
              border:       `1px solid ${T.blue}`,
              background:    T.blue,
              color:         T.white,
              cursor:        busy ? "not-allowed" : "pointer",
              opacity:       busy ? 0.6 : 1,
            }}
          >
            {busy ? "Saving…" : "Save"}
          </button>
          <button
            data-testid="edit-draw-cancel"
            type="button"
            onClick={cancelEdit}
            disabled={busy}
            style={{
              fontSize:   T.fsSm,
              padding:    "3px 8px",
              borderRadius: 4,
              border:     `1px solid ${T.border}`,
              background:  "none",
              color:       T.mid,
              cursor:      "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    );
  }

  // ── Read view ──────────────────────────────────────────────────────────
  return (
    <div
      data-testid="concession-draw-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "4px 0",
        fontSize: T.fsSm,
        color: T.mid,
        borderTop: `0.5px solid ${T.warmBorder}`,
      }}
    >
      <span style={{ minWidth: 90 }}>{fmtDate(date) || "—"}</span>
      <span style={{ fontWeight: T.fwSemibold, color: T.dark }}>
        {fmtAmount(amt, ccy)}
      </span>
      {draw.description && (
        <span style={{
          flex: 1, overflow: "hidden",
          textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {draw.description}
        </span>
      )}
      {draw.recordedAt && (
        <span
          data-testid="draw-recorded-at"
          style={{ fontSize: T.fsXxs, color: T.light, whiteSpace: "nowrap" }}
        >
          {fmtDate(draw.recordedAt)}
        </span>
      )}
      {draw.provenance === "Manual" && (
        <span
          data-testid="draw-provenance-badge"
          title="Manually recorded"
          style={{
            fontSize: T.fsXxs,
            fontWeight: T.fwBold,
            padding: "2px 6px",
            borderRadius: 6,
            background: T.blueSoft,
            color: T.blue,
            flexShrink: 0,
          }}
        >
          M
        </span>
      )}
      {(canEdit || canDelete) && !confirming && (
        <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {canEdit && (
            <button
              data-testid="draw-edit-btn"
              onClick={openEdit}
              title="Edit draw"
              style={{
                background: "none",
                border: "none",
                padding: "0 2px",
                cursor: "pointer",
                color: T.blue,
                fontSize: T.fsXs,
                lineHeight: 1,
              }}
            >
              edit
            </button>
          )}
          {canDelete && (
            <button
              data-testid="draw-delete-btn"
              onClick={() => setConfirming(true)}
              title="Delete draw"
              style={{
                background: "none",
                border: "none",
                padding: "0 2px",
                cursor: "pointer",
                color: T.mid,
                fontSize: T.fsXs,
                lineHeight: 1,
                opacity: 0.6,
              }}
            >
              ✕
            </button>
          )}
        </span>
      )}
      {canDelete && confirming && (
        <span
          data-testid="draw-delete-confirm"
          style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}
        >
          <span style={{ fontSize: T.fsXs, color: T.mid }}>Delete?</span>
          <button
            data-testid="draw-delete-confirm-yes"
            onClick={() => { setConfirming(false); onDelete(draw); }}
            style={{
              fontSize: T.fsXs,
              padding: "1px 8px",
              borderRadius: 3,
              border: `1px solid ${T.red}`,
              background: T.red,
              color: T.white,
              cursor: "pointer",
            }}
          >
            Yes
          </button>
          <button
            data-testid="draw-delete-confirm-cancel"
            onClick={() => setConfirming(false)}
            style={{
              fontSize: T.fsXs,
              padding: "1px 8px",
              borderRadius: 3,
              border: `1px solid ${T.border}`,
              background: "none",
              color: T.mid,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </span>
      )}
    </div>
  );
}

// ─── AddDrawForm ──────────────────────────────────────────────────────────
//
// expired: when true, shows a hint confirming back-dated draws are allowed,
// and references the allowance expiry date.

function AddDrawForm({ locationId, leaseId, allowanceId, currency, remaining, onSaved, onCancel, expired, expiryDate }) {
  const [drawDate,   setDrawDate]   = useState("");
  const [drawAmount, setDrawAmount] = useState("");
  const [drawDesc,   setDrawDesc]   = useState("");
  const [busy,       setBusy]       = useState(false);
  const [error,      setError]      = useState(null);

  // Non-blocking overdraw warning: shown when entered amount > remaining balance.
  // Save is still allowed; the DB constraint is the hard limit.
  const parsedDrawAmt = parseFloat(drawAmount);
  const overdrawWarning =
    remaining != null && Number.isFinite(parsedDrawAmt) && parsedDrawAmt > 0 &&
    Number.isFinite(remaining) && parsedDrawAmt > remaining;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!drawDate) {
      setError(new Error("Date is required."));
      return;
    }
    const amt = parseFloat(drawAmount);
    if (!drawAmount || !Number.isFinite(amt) || amt <= 0) {
      setError(new Error("Amount must be a number greater than 0."));
      return;
    }

    setBusy(true);
    try {
      await recordDraw(locationId, leaseId, allowanceId, {
        drawDate:    drawDate,
        amount:      amt,
        ...(drawDesc.trim() ? { description: drawDesc.trim() } : {}),
      });
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      data-testid="add-draw-form"
      onSubmit={handleSubmit}
      style={{ marginTop: 6 }}
    >
      {expired && expiryDate && (
        <div
          data-testid="add-draw-expired-hint"
          style={{
            fontSize: T.fsXs,
            color: T.mid,
            marginBottom: 6,
            padding: "4px 8px",
            background: T.lightGray,
            borderRadius: 4,
          }}
        >
          ✓ Back-dated draws allowed - date is on/before the {fmtDate(expiryDate)} expiry.
        </div>
      )}
      {overdrawWarning && (
        <div
          data-testid="add-draw-overdraw-warning"
          style={{
            fontSize: T.fsXs,
            color: T.amber,
            marginBottom: 6,
            padding: "4px 8px",
            background: T.pillAmberBg,
            borderRadius: 4,
          }}
        >
          ⚠ Draw amount exceeds the remaining balance ({fmtAmount(remaining, currency)}). Save will be rejected by the server unless the allowance total is updated first.
        </div>
      )}
      {error && (
        <div style={{ marginBottom: 6 }}>
          <ErrorBanner error={error} onDismiss={() => setError(null)} />
        </div>
      )}
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <DateInput
          data-testid="add-draw-date"
          value={drawDate}
          onChange={(e) => setDrawDate(e.target.value)}
          disabled={busy}
          style={{ ...INPUT_STYLE, width: 140 }}
        />
        <input
          data-testid="add-draw-amount"
          type="number"
          placeholder={`Amount (${currency || "USD"})`}
          value={drawAmount}
          onChange={(e) => setDrawAmount(e.target.value)}
          disabled={busy}
          min="0.01"
          step="0.01"
          style={{ ...INPUT_STYLE, width: 140 }}
        />
        <input
          data-testid="add-draw-description"
          type="text"
          placeholder="Description (optional)"
          value={drawDesc}
          onChange={(e) => setDrawDesc(e.target.value)}
          disabled={busy}
          style={{ ...INPUT_STYLE, flex: 1, minWidth: 120 }}
        />
        <button
          data-testid="add-draw-save"
          type="submit"
          disabled={busy}
          style={{
            fontSize:     T.fsSm,
            fontWeight:   T.fwSemibold,
            padding:      "3px 12px",
            borderRadius:  4,
            border:       `1px solid ${T.blue}`,
            background:    T.blue,
            color:         T.white,
            cursor:        busy ? "not-allowed" : "pointer",
            opacity:       busy ? 0.6 : 1,
          }}
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button
          data-testid="add-draw-cancel"
          type="button"
          onClick={onCancel}
          disabled={busy}
          style={{
            fontSize:   T.fsSm,
            padding:    "3px 10px",
            borderRadius: 4,
            border:     `1px solid ${T.border}`,
            background:  "none",
            color:       T.mid,
            cursor:      "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── ConcessionCard ───────────────────────────────────────────────────────
//
// canWrite: when false (viewer role), all write affordances (add/edit/delete)
// are hidden. Defaults to true for backward compat with existing tests and
// callers that don't thread canWrite. LeaseSummaryTab passes canWrite={canWrite}
// explicitly from the role context.

export function ConcessionCard({ allowance, locationId, leaseId, onDrawAdded, canWrite = true, displayCcy, rates }) {
  const [clauseOpen,  setClauseOpen]  = useState(false);
  const [drawsOpen,   setDrawsOpen]   = useState(false);
  const [addDrawOpen, setAddDrawOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const {
    type,
    label,
    totalAmount,
    drawnToDate,
    currency,
    expiryDate,
    notes,
    draws = [],
  } = allowance;

  const bg        = TYPE_BG[type]    || TYPE_BG.other;
  const fg        = TYPE_FG[type]    || TYPE_FG.other;
  const typeLabel = TYPE_LABEL[type] || "Other";
  const rem       = calcRemaining(totalAmount, drawnToDate);
  const days      = daysFromToday(expiryDate);
  const drawCount = draws.length;

  // Forfeiture: expired allowance with undrawn balance.
  // Abatements are auto-applied rent-free periods — they are never drawn and
  // never forfeited in the cash-allowance sense. Guard both values to 0.
  const expired    = isExpired(expiryDate);
  const isAbatement = type === "abatement";
  const forfeited  = !isAbatement && expired && rem != null && rem > 0 ? rem : 0;

  // canAddDraw: non-abatement, non-expired, still has remaining balance, editor/admin only.
  const canAddDraw = canWrite && !isAbatement && !expired && rem != null && rem > 0;

  // Fill bar percentages
  const total = parseFloat(totalAmount);
  const drawn = parseFloat(drawnToDate ?? 0);
  const fillPct = (() => {
    if (!totalAmount || !drawnToDate) return 0;
    if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(drawn)) return 0;
    return Math.min(100, Math.max(0, (drawn / total) * 100));
  })();
  const forfeitPct = (() => {
    if (isAbatement) return 0;
    if (!expired || forfeited <= 0 || !Number.isFinite(total) || total <= 0) return 0;
    return Math.min(100 - fillPct, Math.max(0, (forfeited / total) * 100));
  })();

  async function handleDrawSaved() {
    setAddDrawOpen(false);
    if (typeof onDrawAdded === "function") onDrawAdded();
  }

  // ── Status badge (replaces DaysRemainingPill) ─────────────────────────
  // Expired → red "Expired {Mon DD YYYY}"
  // Active with expiry → green "Draw by {Mon DD YYYY}"
  // No expiry → no badge
  const statusBadge = expiryDate ? (
    expired ? (
      <span
        data-testid="concession-status-badge"
        style={{
          fontSize: T.fsXxs,
          fontWeight: T.fwSemibold,
          padding: "1px 7px",
          borderRadius: 6,
          background: T.pillRedBg,
          color: T.red,
          whiteSpace: "nowrap",
        }}
      >
        Expired {fmtDate(expiryDate)}
      </span>
    ) : (
      <span
        data-testid="concession-status-badge"
        style={{
          fontSize: T.fsXxs,
          fontWeight: T.fwSemibold,
          padding: "1px 7px",
          borderRadius: 6,
          background: T.pillGreenBg,
          color: T.forestGreen,
          whiteSpace: "nowrap",
        }}
      >
        Draw by {fmtDate(expiryDate)}
      </span>
    )
  ) : null;

  return (
    <div
      data-testid="concession-card"
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 6,
        padding: "10px 13px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {/* ── Header: type pill + title + status badge ───────────── */}
      <div style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 8,
      }}>
        <div style={{
          display: "flex", alignItems: "center",
          gap: 8, flexWrap: "wrap", flex: 1,
        }}>
          <span
            data-testid="concession-type-pill"
            style={{
              fontSize: T.fsXxs, fontWeight: T.fwBold,
              textTransform: "uppercase", letterSpacing: 0.4,
              display: "inline-flex", alignItems: "center",
              padding: "2px 8px", borderRadius: 6,
              background: bg, color: fg, whiteSpace: "nowrap",
            }}
          >
            {typeLabel}
          </span>
          <span style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark }}>
            {label || typeLabel}
          </span>
        </div>
        {expiryDate && (
          <div data-testid="concession-expiry-pill" style={{ flexShrink: 0 }}>
            {statusBadge}
          </div>
        )}
      </div>

      {/* LEASE-FIELDS-ADD-DROP: near-expiry nudge hidden (draws UI removed) */}

      {/* ── 3-stat row: Total | Drawn | Remaining/Forfeited ────── */}
      <div
        data-testid="concession-stats"
        style={{ display: "flex", gap: 20 }}
      >
        <div>
          <div style={{
            fontSize: T.fsXxs,
            fontWeight: T.fwSemibold,
            color: T.light,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            marginBottom: 2,
          }}>
            Total
          </div>
          <div
            data-testid="concession-total"
            style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark }}
          >
            {fmtAmount(totalAmount, currency, displayCcy, rates)}
          </div>
        </div>
        <div>
          <div style={{
            fontSize: T.fsXxs,
            fontWeight: T.fwSemibold,
            color: T.light,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            marginBottom: 2,
          }}>
            Drawn
          </div>
          <div
            data-testid="concession-drawn"
            style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark }}
          >
            {fmtAmount(drawnToDate, currency, displayCcy, rates)}
          </div>
        </div>
        <div>
          <div style={{
            fontSize: T.fsXxs,
            fontWeight: T.fwSemibold,
            color: T.light,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            marginBottom: 2,
          }}>
            {expired && forfeited > 0 ? "Forfeited" : "Remaining"}
          </div>
          <div
            data-testid="concession-remaining"
            style={{
              fontSize: T.fsXs,
              fontWeight: T.fwSemibold,
              color: expired && forfeited > 0
                ? T.red
                : (rem != null && rem > 0 ? T.forestGreen : T.dark),
            }}
          >
            {rem != null ? fmtAmount(rem, currency, displayCcy, rates) : "—"}
          </div>
        </div>
      </div>

      {/* ── Spend bar: drawn (blue) + forfeited (hatched red) + track ── */}
      <div
        data-testid="concession-fill-bar-track"
        style={{
          height: 11,
          borderRadius: 6,
          background: T.border,
          overflow: "hidden",
          position: "relative",
          display: "flex",
        }}
      >
        <div
          data-testid="concession-fill-bar"
          style={{
            height: "100%",
            width: `${fillPct}%`,
            background: T.blue,
            borderRadius: "6px 0 0 6px",
            transition: "width 0.3s ease",
            flexShrink: 0,
          }}
        />
        {forfeitPct > 0 && (
          <div
            data-testid="concession-forfeit-bar"
            style={{
              height: "100%",
              width: `${forfeitPct}%`,
              background: `repeating-linear-gradient(
                135deg,
                ${T.red} 0px,
                ${T.red} 3px,
                ${T.pillRedBg} 3px,
                ${T.pillRedBg} 7px
              )`,
              flexShrink: 0,
            }}
          />
        )}
      </div>

      {/* ── Bar legend: drawn amount · available or forfeited ─────── */}
      <div
        data-testid="concession-bar-legend"
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: T.fsXs,
          color: T.mid,
          marginTop: -4,
        }}
      >
        <span>{fmtAmount(drawnToDate, currency, displayCcy, rates)} drawn</span>
        {expired && forfeited > 0
          ? <span style={{ color: T.red }}>{fmtAmount(forfeited, currency, displayCcy, rates)} forfeited (expired undrawn)</span>
          : <span>{rem != null ? fmtAmount(rem, currency, displayCcy, rates) : "—"} available</span>
        }
      </div>

      {/* ── Notes / clause (collapsed by default) ─────────────────── */}
      {notes && (
        <div>
          <button
            data-testid="concession-clause-toggle"
            onClick={() => setClauseOpen((o) => !o)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              fontSize: T.fsXs,
              color: T.mid,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            Allowance terms {clauseOpen ? "▴" : "▾"}
          </button>
          {clauseOpen && (
            <div
              data-testid="concession-notes"
              style={{
                fontSize: T.fsXs,
                color: T.mid,
                whiteSpace: "pre-wrap",
                marginTop: 4,
                paddingLeft: 8,
                borderLeft: `2px solid ${T.border}`,
              }}
            >
              {notes}
            </div>
          )}
        </div>
      )}

      {/* ── Draw history toggle ────────────────────────────────────── */}
      {drawCount > 0 && (
        <div>
          <button
            data-testid="concession-draws-toggle"
            onClick={() => setDrawsOpen((o) => !o)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              fontSize: T.fsXs,
              color: T.mid,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            Draw history ({drawCount}) {drawsOpen ? "▴" : "▾"}
          </button>
          {drawsOpen && (
            <div data-testid="concession-draws-list">
              {draws.map((d) => (
                <DrawHistoryRow
                  key={d.id}
                  draw={d}
                  currency={currency}
                  canEdit={false}
                  canDelete={false}
                  onSaved={() => {}}
                  onDelete={() => {}}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Add draw ───────────────────────────────────────────────── */}
      {canAddDraw && (
        <div>
          {addDrawOpen ? (
            <AddDrawForm
              locationId={locationId}
              leaseId={leaseId}
              allowanceId={allowance.id}
              currency={currency}
              remaining={rem}
              onSaved={handleDrawSaved}
              onCancel={() => setAddDrawOpen(false)}
              expired={expired}
              expiryDate={expiryDate}
            />
          ) : (
            <button
              data-testid="concession-add-draw-button"
              onClick={() => setAddDrawOpen(true)}
              style={{
                background: "none",
                border: `1px solid ${T.blue}`,
                borderRadius: 4,
                padding: "3px 10px",
                fontSize: T.fsXs,
                color: T.blue,
                cursor: "pointer",
                fontWeight: T.fwSemibold,
              }}
            >
              + Record draw
            </button>
          )}
        </div>
      )}

      {deleteError && (
        <ErrorBanner error={deleteError} onDismiss={() => setDeleteError(null)} />
      )}

      {isAbatement && (
        <span
          data-testid="concession-abatement-readonly"
          style={{ fontSize: T.fsXs, color: T.mid, fontStyle: "italic" }}
        >
          Auto-applied - not drawable
        </span>
      )}
    </div>
  );
}
